import {
  type Briefing,
  type BriefingList,
  briefingEstimateSchema,
  briefingFreshnessSchema,
  briefingListSchema,
  briefingSchema,
  type CreateBriefing,
  estateFileListSchema,
} from '@acasa/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { API_PREFIX, apiFetch } from '@/lib/api'

/**
 * Objekt-Briefings.
 *
 * Every key starts with `'briefings'`, but invalidation is aimed at `list` and
 * `detail` only: the file listing and the estimate are live onOffice reads (the
 * estimate even downloads every selected PDF), and a blanket invalidation after
 * a create would spend quota on a dialog that is already closed.
 */
export const briefingKeys = {
  lists: ['briefings', 'list'] as const,
  list: (estateId: number | null) => ['briefings', 'list', estateId ?? 'all'] as const,
  detail: (id: string) => ['briefings', 'detail', id] as const,
  files: (estateId: number) => ['briefings', 'files', estateId] as const,
  estimate: (input: CreateBriefing) =>
    ['briefings', 'estimate', input.estateId, input.fileIds, input.specialQuestions] as const,
  freshness: (id: string) => ['briefings', 'freshness', id] as const,
}

/** A run takes minutes; polled until it settles, then left alone. */
const POLL_MS = 3_000

export function isBusy(briefing: Pick<Briefing, 'status'>): boolean {
  return briefing.status === 'queued' || briefing.status === 'running'
}

export function useBriefings(estateId: number | null = null) {
  return useQuery({
    queryKey: briefingKeys.list(estateId),
    queryFn: () =>
      apiFetch(
        estateId === null ? '/briefings' : `/briefings?estate=${estateId}`,
        briefingListSchema,
      ),
    refetchInterval: (query) =>
      (query.state.data?.briefings ?? []).some(isBusy) ? POLL_MS : false,
  })
}

export function useBriefing(id: string | undefined) {
  return useQuery({
    queryKey: briefingKeys.detail(id ?? ''),
    queryFn: () => apiFetch(`/briefings/${id}`, briefingSchema),
    enabled: Boolean(id),
    refetchInterval: (query) => (query.state.data && isBusy(query.state.data) ? POLL_MS : false),
  })
}

/**
 * onOffice's file listing for the picker. Never refetched by itself — each
 * call is a live onOffice request, and `available: false` is answered with an
 * "Erneut versuchen" the person presses, not a retry loop.
 */
export function useEstateFiles(estateId: number | null) {
  return useQuery({
    queryKey: briefingKeys.files(estateId ?? 0),
    queryFn: () => apiFetch(`/briefings/estates/${estateId}/files`, estateFileListSchema),
    enabled: estateId !== null,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

/**
 * The price of exactly the run the dialog would start.
 *
 * A query keyed on the input rather than a mutation: a slow answer for the
 * selection before last can then never overwrite the current one, and ticking
 * a box back on reuses the figure already fetched instead of downloading the
 * same PDFs again. The caller debounces the input.
 */
export function useBriefingEstimate(input: CreateBriefing | null) {
  return useQuery({
    queryKey: input ? briefingKeys.estimate(input) : ['briefings', 'estimate', 'none'],
    queryFn: () =>
      apiFetch('/briefings/estimate', briefingEstimateSchema, { method: 'POST', body: input }),
    enabled: input !== null,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useCreateBriefing() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateBriefing) =>
      apiFetch('/briefings', briefingSchema, { method: 'POST', body: input }),
    onSuccess: (briefing) => {
      queryClient.setQueryData(briefingKeys.detail(briefing.id), briefing)
      // Show the queued row at once in every list that would contain it.
      for (const estateId of [null, briefing.estate.estateId]) {
        queryClient.setQueryData(
          briefingKeys.list(estateId),
          (previous: BriefingList | undefined) =>
            previous ? { ...previous, briefings: [briefing, ...previous.briefings] } : previous,
        )
      }
      void queryClient.invalidateQueries({ queryKey: briefingKeys.lists })
    },
  })
}

export function useDeleteBriefing() {
  const queryClient = useQueryClient()
  return useMutation({
    // 204 has no body; apiFetch reads that as null, which `z.null()` accepts.
    mutationFn: (id: string) => apiFetch(`/briefings/${id}`, z.null(), { method: 'DELETE' }),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: briefingKeys.detail(id) })
      queryClient.removeQueries({ queryKey: briefingKeys.freshness(id) })
      void queryClient.invalidateQueries({ queryKey: briefingKeys.lists })
    },
  })
}

/**
 * Writes the briefing's address into the object in onOffice. The answer is
 * the briefing as it now stands; the lists are invalidated because linking
 * one briefing can make another of the same object no longer `current`.
 */
export function useLinkBriefingToOnOffice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/briefings/${id}/onoffice-link`, briefingSchema, { method: 'POST' }),
    onSuccess: (briefing) => {
      queryClient.setQueryData(briefingKeys.detail(briefing.id), briefing)
      void queryClient.invalidateQueries({ queryKey: briefingKeys.lists })
      void queryClient.invalidateQueries({ queryKey: ['briefings', 'detail'] })
    },
  })
}

/** One live listing call per opened briefing; not repeated on focus. */
export function useBriefingFreshness(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: briefingKeys.freshness(id ?? ''),
    queryFn: () => apiFetch(`/briefings/${id}/freshness`, briefingFreshnessSchema),
    enabled: Boolean(id) && enabled,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function briefingPdfUrl(id: string): string {
  return `${API_PREFIX}/briefings/${id}/pdf`
}
